export type TtsApi = {
  setRate: (n: number) => void;
  speak: (text: string) => void;
  stop: () => void;
  bindFocus: () => void;
  unbindFocus: () => void;
  bindHover: () => void;
  unbindHover: () => void;
  bindSelection: () => void;
  unbindSelection: () => void;
  readPage: () => void;
};

function supportsSpeech() {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    "SpeechSynthesisUtterance" in window
  );
}

// --- Internal state & handlers
let rate = 1.0;
let focusHandler: ((e: FocusEvent) => void) | null = null;
let hoverHandler: ((e: MouseEvent) => void) | null = null;
let selectionChangeHandler: ((e: Event) => void) | null = null;
let selectionFinalizeMouse: ((e: MouseEvent) => void) | null = null;
let selectionFinalizeKey: ((e: KeyboardEvent) => void) | null = null;

let hoverDebounceId: number | null = null;
let selectionDebounceId: number | null = null;
let lastSpoken = ""; // dedupe repeated utterances

// --- Target filters (what is allowed to read on hover/focus)
const CLICKABLE_SEL =
  "button, a[href], [role=button], [role=link], input, textarea, select, summary, [contenteditable=''], [contenteditable='true']";
const TEXTUAL_TAGS = new Set([
  "P",
  "SPAN",
  "LI",
  "LABEL",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "TD",
  "TH",
  "CAPTION",
  "FIGCAPTION",
]);

// Pull a concise label for an element.
function getLabel(el: HTMLElement | null): string {
  if (!el) return "";
  // ARIA/alt/placeholder first
  const aria =
    el.getAttribute("aria-label") ||
    el.getAttribute("alt") ||
    (el as HTMLInputElement).placeholder ||
    "";
  if (aria && aria.trim()) return aria.trim().slice(0, 200);

  // If it's clickable/focusable, prefer innerText (often short)
  if (el.matches(CLICKABLE_SEL)) {
    const t = (el.innerText || el.textContent || "").trim().replace(/\s+/g, " ");
    return t.slice(0, 200);
  }

  // If it's a simple textual block (short), we allow it
  if (TEXTUAL_TAGS.has(el.tagName)) {
    const t = (el.innerText || el.textContent || "").trim().replace(/\s+/g, " ");
    if (t.length > 2 && t.length <= 180) return t; // avoid reading huge paragraphs on hover
  }

  return "";
}

function speakNow(text: string) {
  if (!supportsSpeech()) return;
  if (!text) return;

  // Dedupe consecutive identical announcements
  if (text === lastSpoken) return;
  lastSpoken = text;

  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = rate;
  window.speechSynthesis.speak(u);
}

export const TTSRuntime: TtsApi = {
  // --- Core API
  setRate(n: number) {
    rate = Math.max(0.5, Math.min(2.0, Number(n) || 1.0));
  },

  stop() {
    if (!supportsSpeech()) return;
    window.speechSynthesis.cancel();
    lastSpoken = "";
  },

  speak(text: string) {
    speakNow(text);
  },

  // --- Focus reading (keyboard navigation)

  bindFocus() {
    this.unbindFocus();
    focusHandler = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      const label = getLabel(target || null);
      if (label) speakNow(label);
    };
    document.addEventListener("focusin", focusHandler, true);
  },

  unbindFocus() {
    if (focusHandler) {
      document.removeEventListener("focusin", focusHandler, true);
      focusHandler = null;
    }
  },

  // --- Hover reading (mouse)

  bindHover() {
    this.unbindHover();
    hoverHandler = (e: MouseEvent) => {
      // throttle / debounce to avoid flooding while moving mouse
      if (hoverDebounceId) {
        window.clearTimeout(hoverDebounceId);
        hoverDebounceId = null;
      }
      const target = e.target as HTMLElement | null;
      hoverDebounceId = window.setTimeout(() => {
        if (!target) return;
        // Only announce for clickable controls OR short text blocks
        if (
          target.matches(CLICKABLE_SEL) ||
          TEXTUAL_TAGS.has(target.tagName) ||
          target.hasAttribute("aria-label") ||
          target.hasAttribute("alt")
        ) {
          const label = getLabel(target);
          if (label) speakNow(label);
        }
      }, 160);
    };
    document.addEventListener("mouseover", hoverHandler, true);
  },

  unbindHover() {
    if (hoverHandler) {
      document.removeEventListener("mouseover", hoverHandler, true);
      hoverHandler = null;
    }
    if (hoverDebounceId) {
      window.clearTimeout(hoverDebounceId);
      hoverDebounceId = null;
    }
  },

  // --- Selection reading (when user highlights text)
  // Note: wired under "Read on Focus" pref, since raw text isn't focusable.

  bindSelection() {
    this.unbindSelection();

    const scheduleReadSelection = () => {
      if (selectionDebounceId) {
        window.clearTimeout(selectionDebounceId);
        selectionDebounceId = null;
      }
      selectionDebounceId = window.setTimeout(() => {
        const sel = window.getSelection?.();
        if (!sel) return;
        const str = sel.toString().trim().replace(/\s+/g, " ");
        if (str.length >= 3 && str.length <= 1200) {
          speakNow(str);
        }
      }, 180);
    };

    selectionChangeHandler = () => scheduleReadSelection();
    selectionFinalizeMouse = () => scheduleReadSelection();
    selectionFinalizeKey = () => scheduleReadSelection();

    document.addEventListener("selectionchange", selectionChangeHandler as EventListener, true);
    document.addEventListener("mouseup", selectionFinalizeMouse, true);
    document.addEventListener("keyup", selectionFinalizeKey as EventListener, true);
  },

  unbindSelection() {
    if (selectionChangeHandler) {
      document.removeEventListener(
        "selectionchange",
        selectionChangeHandler as EventListener,
        true
      );
      selectionChangeHandler = null;
    }
    if (selectionFinalizeMouse) {
      document.removeEventListener("mouseup", selectionFinalizeMouse, true);
      selectionFinalizeMouse = null;
    }
    if (selectionFinalizeKey) {
      document.removeEventListener("keyup", selectionFinalizeKey as EventListener, true);
      selectionFinalizeKey = null;
    }
    if (selectionDebounceId) {
      window.clearTimeout(selectionDebounceId);
      selectionDebounceId = null;
    }
  },

  // --- Read entire page once (when master TTS toggles ON)

  readPage() {
    if (typeof document === "undefined") return;
    // Collect meaningful content in reading order
    const selectors = `
      main, article, section, h1, h2, h3, h4, h5, h6,
      p, li, caption, figcaption, th, td,
      [aria-label], [role=heading]
    `;
    const nodes = Array.from(document.querySelectorAll<HTMLElement>(selectors));

    const parts: string[] = [];
    for (const n of nodes) {
      // Skip hidden/offscreen
      const style = window.getComputedStyle(n);
      if (style.visibility === "hidden" || style.display === "none") continue;

      const aria = n.getAttribute("aria-label") || "";
      const text = (aria || n.innerText || n.textContent || "").trim().replace(/\s+/g, " ");
      if (text && text.length > 2) {
        // Avoid consecutive duplicates
        if (parts.length === 0 || parts[parts.length - 1] !== text) {
          parts.push(text.slice(0, 800)); // guard per node
        }
      }
      if (parts.join(" ").length > 8000) break; // overall guard
    }

    const combined = parts.join(". ");
    if (combined) speakNow(combined);
  },
};
